import { useState } from 'react';
import { C } from '../../../lib/theme';
import { styles } from '../../../lib/styles';
import type { Case, FinancialRef } from '../../../domain/initial-cases';
import { useAppData } from '../../../state/contexts/AppDataContext';
import { useUIState } from '../../../state/contexts/UIStateContext';
import {
  FINANCIAL_REF_MIN_BANKING,
  FINANCIAL_REF_MIN_BUSINESS,
  financialBarMet,
  isVerifiedRef,
} from '../../../domain/financial-bar';
import { KYC_ITEMS } from '../../../domain/constants';
import { kycItemProgress, kycMinimumsApply } from '../../../domain/kyc-progress';

// #695 — THE REFERENCES LIVE INSIDE THE KYC ITEM THEY ARE EVIDENCE FOR.
//
// This was the Financials tab. The KYC checklist has a required "Financial
// Stability" item whose sources literally read "Business References (min 2)"
// and "Banking Reference (min 1)", so the analyst was verifying references on
// one tab and recording the finding on another, with nothing tying the two
// together. They could not agree by construction, and the checkbox was the one
// that fed the decision.
//
// #545's boundary is unchanged and is the reason the badge stays a badge: the
// customer supplies WHO to contact, the ANALYST performs and records the
// verification. A reference the applicant both nominated and vouched for would
// be a self-reported finding, and a self-reported finding is not evidence.
//
// AUDIT CODE. Recording or clearing a verification writes `KYC_UPDATED`, not a
// new code. `FINANCIAL_REF_ADDED` is the wrong verb — nothing was added — and
// adding a code means editing apps/api plus the nine files CLAUDE.md lists,
// which #695 rules out as a data change. `KYC_UPDATED` is also honest about
// what the write IS: the Financial Stability finding, recorded against the
// reference it came from. The detail names the reference so the row reads on
// its own.
export function FinancialReferencesPanel({ c }: { c: Case }) {
  const { updateCase } = useAppData();
  const { setModal } = useUIState();

  // Which reference's verification form is open, by index. One at a time, so
  // one set of fields serves them all — the same shape the KYC evidence form
  // uses on the tab this panel now sits in.
  const [editing, setEditing] = useState<number | null>(null);
  const [vDate, setVDate] = useState('');
  const [vSummary, setVSummary] = useState('');
  const [vError, setVError] = useState<string | null>(null);

  // Defaulted the way KycTab defaults `c.kyc[key]`: the column is NOT NULL on
  // the server, but a case object assembled locally (a fixture, a row merged
  // from a list query that projected fewer columns) can reach a tab without
  // it, and a required KYC item is the wrong place to throw.
  const refs = c.financialRefs ?? [];

  const openForm = (i: number, ref: FinancialRef) => {
    setEditing(i);
    setVDate(ref.date ?? '');
    setVSummary(ref.summary ?? '');
    setVError(null);
  };
  const closeForm = () => { setEditing(null); setVDate(''); setVSummary(''); setVError(null); };

  // ONE PATCH per logical action. The reference write and the KYC tick are the
  // same act — "382 has now called enough references" — so they go together or
  // the checklist disagrees with the evidence again, which is the bug #695
  // exists to close. `kyc` is sent only when the flag actually changes: a
  // verification that leaves the bar unmet has no business rewriting the whole
  // kyc column.
  const writeRefs = (nextRefs: FinancialRef[], detail: Record<string, unknown>) => {
    const kycField = c.kyc?.financial ?? { verified: false, evidence: [] };
    const was = kycField.verified ?? false;
    // Auto-verify ticks. Clearing a verification so the bar is no longer met
    // does NOT untick — the analyst's judgement stands until the analyst
    // changes it, and the checkbox on the item is still theirs. Same asymmetry
    // as #689: a completed verification checks the box, an incomplete one
    // leaves it exactly as they had it.
    //
    // #772 — with ONE exception: clearing the last verified reference takes the
    // item below its minimum (one verified reference), and an item below its
    // minimum cannot stand as verified. A grandfathered case keeps the old rule.
    const belowMinimum = kycMinimumsApply(c)
      && !kycItemProgress(KYC_ITEMS.financial, kycField, nextRefs).met;
    const verified = belowMinimum ? false : (was || financialBarMet(nextRefs));
    updateCase(c.id, {
      financialRefs: nextRefs,
      ...(verified !== was ? { kyc: { ...c.kyc, financial: { ...kycField, verified } } } : {}),
      action: {
        code: 'KYC_UPDATED',
        detail: { item: 'financial', label: 'Financial Stability', ...detail, verified },
      },
    });
  };

  const verifiedCount = (type: string) =>
    refs.filter((r) => r.type === type && isVerifiedRef(r)).length;
  const barMet = financialBarMet(refs);

  return (
    <div data-testid="financial-references" style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, color: C.textSecondary }}>
        Financial References (minimum {FINANCIAL_REF_MIN_BUSINESS + FINANCIAL_REF_MIN_BANKING}: {FINANCIAL_REF_MIN_BUSINESS} business + {FINANCIAL_REF_MIN_BANKING} banking)
      </div>
      {/* The count that decides the auto-tick, said out loud. An analyst
          looking at five references should not have to work out which of them
          382 has actually called. */}
      <div style={{ fontSize: 11, marginBottom: 8, color: barMet ? C.success : C.warn, fontWeight: 600 }}>
        {verifiedCount('business')} of {FINANCIAL_REF_MIN_BUSINESS} business and {verifiedCount('banking')} of {FINANCIAL_REF_MIN_BANKING} banking verified by 382
        {barMet && ' — bar met'}
      </div>
      {refs.length === 0 && (
        <div style={{ fontSize: 12, color: C.textMuted, padding: '8px 0' }}>
          No financial references on this case yet.
        </div>
      )}
      {refs.map((ref, i) => {
        // Absent `source` means an analyst added it — which is every reference
        // that existed before #545, so absent has to read as 382's own.
        const fromApplicant = ref.source === 'customer';
        const verified = isVerifiedRef(ref);
        return (
          <div key={ref.id ?? i} style={{ padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
            <span style={{ ...styles.tag, background: ref.type === 'banking' ? C.infoBg : C.goldBg, color: ref.type === 'banking' ? C.info : C.goldText }}>{ref.type}</span>
            {fromApplicant && (
              <span style={{ ...styles.tag, background: C.infoBg, color: C.info, marginLeft: 6 }}>
                applicant-supplied
              </span>
            )}
            {/* #545 — joined rather than interpolated raw. A name-only
                reference is legal (the portal permits it and docs/intake-api.md
                advertises title/company as optional), and the raw form rendered
                it to an analyst as "Bev Banker — , ". */}
            <strong> {ref.name}</strong>
            {[ref.title, ref.company].some((v) => v)
              && <> — {[ref.title, ref.company].filter((v) => v).join(', ')}</>}
            {/* Applicant-supplied references carry how to reach the person;
                staff-added ones never had this field. */}
            {ref.contact && (
              <div style={{ fontSize: 11, color: C.textMuted }}>Contact: {ref.contact}</div>
            )}
            <div style={{ fontSize: 11, color: C.textMuted }}>
              {verified
                ? <>Date: {ref.date} · {ref.summary}</>
                : (
                  <span style={{ color: C.warn }}>
                    Not yet verified by 382 — no contact recorded.
                    {fromApplicant && ' The applicant nominated this reference; 382 has not called it.'}
                  </span>
                )}
            </div>
            {/* The visible label is the same on every row, so the accessible
                name carries the reference it acts on — a screen reader listing
                the buttons on a five-reference case would otherwise read five
                identical ones. Each accessible name STARTS with the visible
                text, which is what WCAG 2.5.3 Label in Name asks for, so voice
                control still activates them by what is on screen. */}
            <div style={{ marginTop: 6 }}>
              <button
                type="button"
                style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                onClick={() => (editing === i ? closeForm() : openForm(i, ref))}
                aria-expanded={editing === i}
                aria-label={`${verified ? 'Edit' : 'Record'} verification for ${ref.name}`}
              >
                {verified ? 'Edit verification' : 'Record verification'}
              </button>
              {verified && (
                <button
                  type="button"
                  style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall, marginLeft: 8 }}
                  aria-label={`Clear verification for ${ref.name}`}
                  onClick={() => {
                    closeForm();
                    writeRefs(
                      refs.map((r, j) => (j === i ? { ...r, date: '', summary: '' } : r)),
                      { ref: { name: ref.name, type: ref.type }, verification: 'cleared' },
                    );
                  }}
                >
                  Clear verification
                </button>
              )}
            </div>
            {editing === i && (
              <div style={{ marginTop: 8, background: C.white, border: `2px dashed ${C.border}`, borderRadius: 8, padding: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
                  <div>
                    <label htmlFor={`fin-ref-date-${i}`} style={{ fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 3 }}>Date of Contact</label>
                    <input id={`fin-ref-date-${i}`} type="date" style={{ ...styles.input, fontSize: 12, padding: '8px 10px' }} value={vDate} onChange={(e) => { setVDate(e.target.value); if (vError) setVError(null); }} />
                  </div>
                  <div>
                    <label htmlFor={`fin-ref-summary-${i}`} style={{ fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 3 }}>Summary of Discussion</label>
                    <textarea
                      id={`fin-ref-summary-${i}`}
                      style={{ ...styles.textarea, fontSize: 12, minHeight: 56, ...(vError ? { borderColor: C.danger } : {}) }}
                      placeholder="Who did you speak to, and what did they say?"
                      value={vSummary}
                      aria-invalid={vError ? true : undefined}
                      aria-describedby={vError ? `fin-ref-error-${i}` : undefined}
                      onChange={(e) => { setVSummary(e.target.value); if (vError) setVError(null); }}
                    />
                  </div>
                </div>
                {vError && <div id={`fin-ref-error-${i}`} role="alert" style={{ fontSize: 11, color: C.danger, marginTop: 4 }}>{vError}</div>}
                <button
                  style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall, marginTop: 10 }}
                  onClick={() => {
                    // Either field is a verification, so requiring both would
                    // reject records the modal has always accepted. Requiring
                    // NEITHER would let an empty save tick a required KYC item,
                    // which is the failure this whole issue is about.
                    if (!vDate.trim() && !vSummary.trim()) {
                      setVError('Record the date you called, a summary of what was said, or both.');
                      return;
                    }
                    writeRefs(
                      refs.map((r, j) => (j === i ? { ...r, date: vDate, summary: vSummary } : r)),
                      { ref: { name: ref.name, type: ref.type }, verification: 'recorded' },
                    );
                    closeForm();
                  }}
                >
                  Save Verification
                </button>
              </div>
            )}
          </div>
        );
      })}
      <button style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall, marginTop: 12 }} onClick={() => setModal('add-financial-ref')}>+ Add Reference</button>
    </div>
  );
}
